/**
 * Format API `available:false` / exception text for Color Baskets UI + console.
 * @param {{ available?: boolean, reason?: string, tileSize?: number, pieceCount?: number, message?: string }|null|undefined} payload
 * @param {string} [fallback]
 */
export function formatColorBasketsUnavailableReason(payload, fallback = "unknown") {
    const code =
        (typeof payload?.reason === "string" && payload.reason.trim()) ||
        (typeof payload?.message === "string" && payload.message.trim()) ||
        "";
    const tile = Number(payload?.tileSize);
    const pieces = Number(payload?.pieceCount);
    const detailParts = [];
    if (Number.isFinite(tile) && tile > 0) detailParts.push(`tileSize=${tile}`);
    if (Number.isFinite(pieces) && pieces > 0) detailParts.push(`pieces=${pieces}`);
    const detail = detailParts.length ? ` (${detailParts.join(", ")})` : "";

    const labels = {
        grid_too_small: "grid too small (needs Medium 9×9 / 81+ pieces)",
        no_source_image: "no source image (story/jigsaw imageUrl missing or failed to load)",
    };

    if (code && labels[code]) {
        return `${labels[code]}${detail} [${code}]`;
    }
    if (code) {
        return `${code}${detail}`;
    }
    return `${fallback}${detail}`;
}

/**
 * Compact multi-line diagnostics for the color-basket select popup.
 * Prefer these over bare `available=true · reason=null` — they explain map/list visibility.
 */
export function formatColorBasketDebugLines({
    meta = null,
    result = null,
    basketCount = null,
    grid = null,
    imageUrl = null,
    error = null,
    sessionByIndex = null,
} = {}) {
    const lines = [];
    const gridN = Number(grid);
    const tile = Number(result?.tileSize ?? meta?.tileSize);
    const pieces = Number(result?.pieceCount ?? meta?.pieceCount);
    const allowed = Array.isArray(result?.allowedCounts)
        ? result.allowedCounts
        : Array.isArray(meta?.allowedCounts)
          ? meta.allowedCounts
          : [];
    const freeMax = Number(result?.freeMaxCount ?? meta?.freeMaxCount);
    const unlockCost = Number(result?.unlockCoinCost ?? meta?.unlockCoinCost);
    const baskets = Array.isArray(result?.baskets) ? result.baskets : [];

    if (error) {
        lines.push(`error: ${String(error).replace(/\s+/g, " ").trim()}`);
    }

    lines.push(
        [
            `meta.available=${meta == null ? "—" : String(meta.available)}`,
            `result.available=${result == null ? "—" : String(result.available)}`,
            `meta.reason=${meta?.reason ?? "null"}`,
            `result.reason=${result?.reason ?? "null"}`,
        ].join(" · "),
    );

    lines.push(
        [
            `K=${basketCount ?? "—"}`,
            `allowed=${allowed.length ? allowed.join(",") : "—"}`,
            `freeMax=${Number.isFinite(freeMax) ? freeMax : "—"}`,
            `unlockCost=${Number.isFinite(unlockCost) ? unlockCost : "—"}`,
        ].join(" · "),
    );

    lines.push(
        [
            `tileSize=${Number.isFinite(tile) && tile > 0 ? tile : "—"}`,
            `pieceCount=${Number.isFinite(pieces) && pieces > 0 ? pieces : "—"}`,
            `gridProp=${Number.isFinite(gridN) && gridN > 0 ? gridN : "—"}`,
            `image=${imageUrl && String(imageUrl).trim() ? "yes" : "no"}`,
            Number.isFinite(tile) &&
            tile > 0 &&
            Number.isFinite(gridN) &&
            gridN > 0 &&
            tile !== gridN
                ? "GRID≠tileSize"
                : null,
        ]
            .filter(Boolean)
            .join(" · "),
    );

    lines.push(
        `basketsReturned=${baskets.length}${
            basketCount != null && baskets.length !== Number(basketCount)
                ? ` (expected K=${basketCount} — MISMATCH: unlock/play keys use K; regenerate or pick Baskets=${baskets.length})`
                : ""
        }`,
    );

    if (baskets.length > 0) {
        const cellMax =
            Number.isFinite(gridN) && gridN > 0 ? gridN * gridN : Number.POSITIVE_INFINITY;
        const perBasket = baskets
            .slice()
            .sort((a, b) => Number(a?.index) - Number(b?.index))
            .map((b) => {
                const idx = Number(b?.index);
                const ids = Array.isArray(b?.pieceIds) ? b.pieceIds : [];
                const valid = ids.filter((raw) => {
                    const id = Number(raw);
                    return Number.isInteger(id) && id >= 0 && id < cellMax;
                }).length;
                const unlocked =
                    b?.unlocked === true || b?.requiresUnlock === false
                        ? "free/unlocked"
                        : b?.requiresUnlock === true || b?.unlocked === false
                          ? "locked"
                          : "—";
                const session = sessionByIndex?.[idx];
                const playState = session
                    ? String(session.playState || session.play_state || "").trim() || "session"
                    : "no-session";
                const mapNote =
                    Number.isFinite(gridN) && gridN > 0 && valid !== ids.length
                        ? ` map=${valid}/${ids.length}`
                        : ` pcs=${ids.length}`;
                return `#${idx + 1}[${unlocked},${playState}${mapNote}]`;
            });
        lines.push(perBasket.join(" · "));

        const invisibleOnMap = baskets.filter((b) => {
            if (!(Number.isFinite(gridN) && gridN > 0)) return false;
            const ids = Array.isArray(b?.pieceIds) ? b.pieceIds : [];
            const valid = ids.filter((raw) => {
                const id = Number(raw);
                return Number.isInteger(id) && id >= 0 && id < gridN * gridN;
            }).length;
            return valid === 0;
        });
        if (invisibleOnMap.length > 0) {
            lines.push(
                `not on mini-map: ${invisibleOnMap
                    .map((b) => `#${Number(b?.index) + 1}`)
                    .join(",")} (0 pieces inside grid ${gridN}×${gridN})`,
            );
        }
    }

    return lines;
}

/**
 * User-facing unavailable line + console warn with full payload.
 */
export function reportColorBasketsUnavailable(payload, context = "ColorBaskets") {
    const reason = formatColorBasketsUnavailableReason(payload);
    // Always print so promo / local debugging has a trail without BE logs.
    // eslint-disable-next-line no-console
    console.warn(`[${context}] Color baskets unavailable:`, reason, payload ?? null);
    return reason;
}
